/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

// const Hammer = require("js/vendor/hammer/hammer");
import * as Hammer from 'hammerjs';
import { logger } from '@touchplan/logging';
import { HammerConnector } from '@appCanvasCommon/interaction/HammerConnector';

const setTimeout = window.__zone_symbol__setTimeout ? window.__zone_symbol__setTimeout : window.setTimeout;

//prepend hammer- when listening
const supportedEvents = [
	'singletap',
	'doubletap',
	// 'panstart',
	// 'pan',
	// 'panend',
	'pinchstart',
	'pinch',
	'pinchend',
	'press',
	'pressup',
	'hammer.input',
	'singletap2',
];

//start by ripping off the old "feel"
const defaultHammerConfig = {
	recognizers: [
		[Hammer.Pinch],
		// [Hammer.Pan],
		// [Hammer.Tap, {event: 'doubletap', taps: 2, threshold: 7, posThreshold: 25}],
		// [Hammer.Tap, {event: 'singletap', threshold: 7}],
		[Hammer.Press, { time: 333, threshold: 3 }],
	],
};
const singleTap = new Hammer.Tap({ event: 'singletap', threshold: 7 });
const singleTap2 = new Hammer.Tap({
	event: 'singletap2',
	pointers: 2,
	threshold: 10,
	posThreshold: 25,
	time: 333,
});
const doubleTap = new Hammer.Tap({ event: 'doubletap', taps: 2, threshold: 7, posThreshold: 25 });

doubleTap.recognizeWith(singleTap);
singleTap.requireFailure(doubleTap);

const simpleDrag = new Hammer.Pan();
supportedEvents.push('panstart', 'pan', 'panend');

const holdDrag = new Hammer.Pan({ event: 'holddrag' });
supportedEvents.push('holddragstart', 'holddrag', 'holddragend');

holdDrag.recognizeWith(simpleDrag);
// holdDrag.dropRecognizeWith(simpleDrag);
// simpleDrag.dropRecognizeWith(holdDrag);

/**
 * Ties the hammer connector to the pixi instance and hammer.
 * @param  app PIXI.Application
 * @return     The connector, use to destroy.
 *
 * ## Usage
 * ### initialize with the rest of the initializers
 * ```
 * HammerWrapper.init(app);
 * ```
 * ### use on pixi things
 * ```
 * let backgroundGestureContainer = new Container();
 * backgroundGestureContainer.hitArea = new Rectangle(0,0, this.renderer.view.width, this.renderer.view.height);
 * backgroundGestureContainer.eventMode = EventMode.Static;
 * this.stage.addChild(backgroundGestureContainer);
 *
 * backgroundGestureContainer.on('hammer-singletap', (e)=>{
 * 	logger.log('woo');
 * })
 * ```
 */
export function init(app) {
	const hammertime = new Hammer.Manager(app.view, defaultHammerConfig as any);
	hammertime.add([doubleTap, singleTap, singleTap2]);

	//Some ridiculous shit here... "exactly" what's going on...
	//- add the two events that need to be handled with the one that can fail from a different event first
	//- check for the failure condition of the first (the different one), if it's hit, remove the first event entirely
	//- checkfor the failure condition of the second (the shared event). If hit, unbind the recognizeWith
	//- at the conclusion of all events, reset back to the initial state
	hammertime.add([simpleDrag, holdDrag]);
	//press detected, remove simpleDrag
	hammertime.on('press', (evt) => {
		lastStartEventLogging(evt);
		holdDrag.dropRecognizeWith(simpleDrag);
		simpleDrag.dropRecognizeWith(holdDrag);
		hammertime.remove(simpleDrag);
	});
	//simple drag started, unlink holdDrag
	hammertime.on('panstart', (evt) => {
		lastStartEventLogging(evt);
		holdDrag.dropRecognizeWith(simpleDrag);
		simpleDrag.dropRecognizeWith(holdDrag);
	});

	hammertime.on('panstart holddragstart', lastStartEventLogging);

	function lastStartEventLogging(evt) {
		const cleanEvent = safeEventCloneForReporting(evt);
		// logger.log('lastStartEvent', cleanEvent);
		window.lastStartEvent = {
			type: evt.type,
			timestamp: Date.now(),
			raw: cleanEvent,
		};
	}

	hammertime.on('panstart', (evt) => {
		app.renderer.events.startEventAlreadyDetected = true;
	});

	//reset things
	hammertime.on('hammer.input', (evt) => {
		if (evt.isFirst) {
			if (window.getSelection) {
				window.getSelection().removeAllRanges();
			}
		}
		if (evt.isFinal) {
			app.renderer.events.startEventAlreadyDetected = false;
			//this needs to execute before the other hammer.input event internal to the connector
			setTimeout(() => {
				hammertime.remove(holdDrag);
				hammertime.add([simpleDrag, holdDrag]);
				holdDrag.recognizeWith(simpleDrag);
			});
		}
	});

	//End of ridiculous shit
	function safeEventCloneForReporting(evt) {
		const thing = Object.assign({}, evt);
		['changedPointers', 'currentTarget', 'preventDefault', 'srcEvent', 'target', 'pointers'].forEach(
			(key) => delete thing[key]
		);

		if (evt.pointers && evt.pointers.length !== undefined) {
			thing.pointerCount = evt.pointers.length;
		}
		return thing;
	}

	logger.log('[HammerWrapper] canvas hammer');
	const c = new HammerConnector(app.view, app.renderer.events, app.stage, hammertime);
	c.registerHandlerTypes(supportedEvents);
	return c;
}
